import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import FormModalLayout from '../../layouts/FormModalLayout';
import { paymentSettingsSchema } from '../../../schema';
import type { input, output } from 'zod';
import { Control, FieldErrors } from 'react-hook-form';
import { SelectOption, StyleObj } from '../../../@types';
import { PAYMENT_LIMIT_NAME } from '../../../constants/enums';
import PaymentSettingRow from '../../molecules/PaymentSettingRow';
import FormSelect from '../../molecules/FormSelect';

const formConfig = [
  {
    label: 'Min. Amount',
    name: PAYMENT_LIMIT_NAME.MIN_AMOUNT_PER_TRANSACTION,
    allowDecimals: true,
  },
  {
    label: 'Max. Amount',
    name: PAYMENT_LIMIT_NAME.MAX_AMOUNT_PER_TRANSACTION,
    allowDecimals: true,
  },
  {
    label: 'Max. Daily Amount',
    name: PAYMENT_LIMIT_NAME.MAX_DAILY_AMOUNT,
    allowDecimals: true,
  },
  {
    label: 'Max. Weekly Amount',
    name: PAYMENT_LIMIT_NAME.MAX_WEEKLY_AMOUNT,
    allowDecimals: true,
  },
  {
    label: 'Max. Monthly Amount',
    name: PAYMENT_LIMIT_NAME.MAX_MONTHLY_AMOUNT,
    allowDecimals: true,
  },
  {
    label: 'Max. Daily Number',
    name: PAYMENT_LIMIT_NAME.MAX_DAILY_COUNT,
    allowDecimals: false,
  },
  {
    label: 'Max. Weekly Number',
    name: PAYMENT_LIMIT_NAME.MAX_WEEKLY_COUNT,
    allowDecimals: false,
  },
  {
    label: 'Max. Monthly Number',
    name: PAYMENT_LIMIT_NAME.MAX_MONTHLY_COUNT,
    allowDecimals: false,
  },
] as const;

const styles: StyleObj = {
  container: { maxWidth: 800 },
  tableBody: {
    border: 2,
    borderColor: 'background.lighter',
    '& .MuiTableCell-root': {
      p: { xs: 0.25, md: 1 },
      textAlign: 'center',
    },
  },
  approvalModeRow: {
    backgroundColor: 'background.lighter',
  },
  approvalModeSelect: {
    pl: 2.5,
    '.MuiOutlinedInput-notchedOutline': {
      borderStyle: 'none',
    },
  },
  approvalModeSelectInput: {
    textAlign: 'center',
    fontSize: { xs: 12, md: 14 },
    py: 0,
  },
};

type PaymentSettingsFormProps = {
  control: Control<input<typeof paymentSettingsSchema>, unknown, output<typeof paymentSettingsSchema>>;
  errors: FieldErrors<input<typeof paymentSettingsSchema>>;
  onSave: () => void;
  approvalModeOptions: SelectOption[];
  currency?: string;
  isDirty?: boolean;
  disableInputs?: boolean;
  hideInputs?: boolean;
};

const PaymentSettingsForm = ({
  control,
  errors,
  onSave,
  isDirty,
  disableInputs = false,
  hideInputs = false,
  approvalModeOptions,
  currency,
}: PaymentSettingsFormProps) => {
  return (
    <Box sx={styles.container}>
      {currency && <Typography variant='body2'>Amount limits are in {currency}.</Typography>}
      <FormModalLayout disableSubmit={!isDirty} onSave={onSave} disablePadding>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align='center'>Setting</TableCell>
                <TableCell align='center'>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={styles.tableBody}>
              <TableRow>
                <TableCell align='center' sx={styles.approvalModeRow}>
                  Approval Mode
                </TableCell>
                <TableCell align='center'>
                  <FormSelect
                    control={control}
                    name='approvalMode'
                    options={approvalModeOptions}
                    error={errors.approvalMode}
                    allowDeselect={false}
                    MenuProps={{
                      slotProps: { list: { dense: true } },
                    }}
                    sx={styles.approvalModeSelect}
                    inputProps={{
                      sx: styles.approvalModeSelectInput,
                    }}
                  />
                </TableCell>
              </TableRow>

              {!hideInputs &&
                formConfig.map((field) => (
                  <PaymentSettingRow
                    key={field.name}
                    field={field}
                    control={control}
                    errors={errors}
                    disabled={disableInputs}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </FormModalLayout>
    </Box>
  );
};

export default PaymentSettingsForm;
